import { cloneDeep } from 'lodash';
import { createVueRouter, initializeVueRouteGuards } from 'client/AdminPortal/router/Routes';
import { hookEvents } from 'client/GrowthEngineering/EventWatcher';
import { setupAxios } from 'client/api/v2/interceptors';
import { setupEventTracking } from '@jumpcloud/ui-events-vue';
import AdminPortalApp from 'client/AdminPortalApp';
import ConfigService from 'client/util/ConfigService';
import Constants, {
  EventConsts,
  LoginConsts,
  UserRoleConsts,
} from 'client/util/Constants';
import Cookies from 'js-cookie';
import DataDog from 'client/util/monitoring/DataDog';
import EventManager from 'client/GrowthEngineering/EventManager';
import InactivityModalController from 'client/components/Modal/InactivityModalController';
import LocalStorageService from 'client/util/LocalStorageService';
import LoggingService from 'client/util/LoggingService';
import MspState from 'client/components/models/MspState';
import OptimizelyApi from 'client/GrowthEngineering/OptimizelyApi';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import Router from 'client/layout/views/Router';
import Util from 'client/util/Util';
import Viewport from 'client/layout/views/Viewport';
import Vue from 'vue';
import globalRouteService from 'client/AdminPortal/router/globalRouteService';
import hashString from 'client/GrowthEngineering/utilities/hashString';
import i18n, { loadAdminPortalLocales } from 'client/i18n';
import store from 'client/store';
import util from 'client/util';

const {
  updateLastActiveTime,
} = InactivityModalController;

const featureTrialModule = 'FeatureTrialModule';
const growthModule = 'GrowthModule';
const onboardingModule = 'OnboardingModule';
const partnersModule = 'PartnersModule';
let settings;
let MtpRootVueComponent;

export const initializeSubscribeAction = (pageNavigationUtil = PageNavigationUtil) => ({
  after(action) {
    let logAdminEvent = false;

    switch (action.type) {
      case 'ProviderModel/createUpdateProviderAdmin':
      case 'UserCollection/createNewAdministrator':
      case 'UserCollection/updateAdministrator':
        logAdminEvent = true;
        break;

      default:
        break;
    }

    // Trigger a storage event so that all open MTP tabs can refresh
    // and then remove so it doesn't pollute localStorage
    if (logAdminEvent) {
      pageNavigationUtil.setLocalStorageItem(EventConsts.adminEvent, true);
      pageNavigationUtil.removeLocalStorageItem(EventConsts.adminEvent);
    }
  },
});

function getSegmentWriteKey() {
  const writeKeysByEnv = {
    [Constants.environments.local]: 'JOpuhvbG8TI3ZE1hwKQPwfJTdL5ghOmg',
    [Constants.environments.staging]: '0T2793Yu6tHDae2Dh6HQDzvcGg9PmC89',
    [Constants.environments.production]: '5WxRdxmQuMf3bEtWRVEZLVBjYIrXUb1I',
  };
  return writeKeysByEnv[Util.getEnvironment()] || '';
}

export function createInitializationHandler(pageNavigationUtil = PageNavigationUtil) {
  return {
    /**
     * Wipe admin portal. This is used for transitioning to MTP
     */
    clearAdminPortal() {
      // Clear out all content
      document.body.innerHTML = '';
      MspState.clearOrgId();
      pageNavigationUtil.removeEventHandlers();
      if (window.viewport) {
        window.viewport.resetAdminConsole();
      }
    },

    /**
     * Wipe MTP and all Vue instances. This is used for transitioning to admin portal
     */
    clearMtp() {
      document.body.innerHTML = '';
      if (MtpRootVueComponent && MtpRootVueComponent.$destroy) {
        MtpRootVueComponent.$destroy();
      }
    },

    /**
     * NOTE: This is an internal method of the exported module so that it can be more easily mocked
     * in unit tests. This avoids initializing the GrowthModule, OrganizationsCollection, etc.
     */
    async initializeOnboarding() {
      const { adminGrowthData } = store.state[onboardingModule];
      const newAdminGrowthData = cloneDeep(adminGrowthData);

      // This is a way for us to reset the checklist for testing intead of using curl
      const resetKey = 'jcResetOnboarding';
      const resetOnboarding = localStorage.getItem(resetKey) === 'true';

      if (resetOnboarding) {
        const { onboardingTasks } = newAdminGrowthData.onboardingState;

        Object.keys(onboardingTasks || {}).forEach((key) => {
          newAdminGrowthData.onboardingState.onboardingTasks[key] = false;
        });
        newAdminGrowthData.onboardingState.chatSeen = false;
        newAdminGrowthData.onboardingState.onboardingModalSeen = false;
        newAdminGrowthData.onboardingState.productTourSeen = false;
        newAdminGrowthData.onboardingState.selectedJob = null;
        newAdminGrowthData.onboardingState.simplifiedOnboardingDismissed = false;
        newAdminGrowthData.onboardingState.taskChecklistComplete = false;
        // reset implementation checklist
        newAdminGrowthData.onboardingState.implementationChecklists = [];
        newAdminGrowthData.onboardingState.implementationChecklistDismissed = false;

        localStorage.removeItem(resetKey);
      }

      store.dispatch(`${onboardingModule}/setAdminGrowthData`, { growthData: newAdminGrowthData });
      await store.dispatch(`${onboardingModule}/setShowOnboarding`);
    },

    /**
     * NOTE: This is an internal method of the exported module so that it can be more easily mocked
     * in unit tests. This avoids initializing the GrowthModule, OrganizationsCollection, etc.
     */
    initializeGrowthExperiments() {
      const { orgGrowthData } = store.state[growthModule];
      const orgGrowthDataClone = cloneDeep(orgGrowthData);
      const newOrgGrowthData = OptimizelyHelper
        .checkOptimizelyAndBucketUserIntoExperiments(orgGrowthDataClone);

      const isAdmin = store.getters['SettingsModule/isAdmin'];

      if (isAdmin) {
        store.dispatch(`${growthModule}/setOrgGrowthData`, { growthData: newOrgGrowthData });
        store.dispatch(`${featureTrialModule}/setOrgGrowthData`, { growthData: newOrgGrowthData });
      }
      store.dispatch(`${partnersModule}/fetchPartners`);
    },

    // Initialize Growth Engineering libraries
    async initializeGrowthSettings() {
      const { orgId } = store.state.SettingsModule;

      /**
       * Set hashed orgId to local storage to set the Optimizely ID
       * This needs to be set in local storage before Optimizely initializes
       * @see https://support.optimizely.com/hc/en-us/articles/4411652974477-Bring-your-own-visitor-ID-BYOID-
       */
      LocalStorageService.setItem('visitorId', hashString(orgId));

      try {
        // load Optimizely first
        OptimizelyApi.loadOptimizely();
        this.initializeOptimizely();

        const isCommandRunner = settings.user.roleName.indexOf(UserRoleConsts.commandRunner) > -1;

        if (!ConfigService.disableAnalytics && !isCommandRunner) {
          try {
            setupEventTracking({
              writeKey: getSegmentWriteKey(),
              userId: settings.user.id,
              groupBy: 'orgId',
              traits: {
                hashed: {
                  orgId: settings.orgId,
                },
                role: settings.role,
              },
            }, ({ isMobile }) => {
              store.dispatch(`${growthModule}/updateIsMobile`, isMobile);
            });
          } catch (err) {
            // don't do anything, just catch the error so we can move on without breaking
            console.error(err); // eslint-disable-line no-console
          }

          EventManager.setStore(store);
        }
      } catch (e) {
        // eslint-disable-next-line  no-console
        console.error(`Error initializing Growth libraries: ${e.toString()}`);
      }

      hookEvents();
    },

    /**
     * Initialize settings to determine whether an admin is an MSP user.
     * Update the settings store and MSP state appropriately.
     */
    async initializeSettingsAndSetMspState() {
      try {
        await ConfigService.initialize();
      } catch (exp) {
        // eslint-disable-next-line  no-console
        console.error(`Unable to get UI settings! There may be subtle failures in the UI. ${exp.toString()}`);
      }

      setupAxios();

      // If we have an admin route on page load then we need to see if they are
      // logged in as an admin before we send them to their cookie's default.
      const adminRoute = pageNavigationUtil.getLocationHashPath().replace(/^\/$/, '');
      const cookieType = Cookies.get(LoginConsts.clientTypeCookie);
      if (!adminRoute && cookieType !== LoginConsts.clientType.admin) {
        // page load wants user
        pageNavigationUtil.setLocation(LoginConsts.loginRoutes.user);
        // Stop initializing.
        return Promise.reject();
      }

      DataDog.initialize({ serviceName: 'admin-portal' });

      return new Promise((resolve, reject) => store.dispatch('SettingsModule/fetchUserSettings')
        .then(() => Promise.all([
          store.dispatch('OrganizationsCollection/fetchCollection'),
          store.dispatch('SettingsModule/fetchSettings'),
          store.dispatch('SettingsModule/fetchTopLevelSettings'),
          store.dispatch('SettingsModule/getProviderDetails'),
        ])
        // featureFlags depends on fetchSettings
          .then(resp => (
            store.dispatch('FeatureFlagsModule/fetchFeatureFlags')
              .then(() => resp)
          ))
          .then(resp => this.updateSettingsAndMspState(resp))
        // organization details depend on fetchSettings
          .then(() => {
            const { orgId } = store.state.SettingsModule;
            return store.dispatch('OrganizationsCollection/getDetailsForOrganization', { id: orgId });
          })
        // Fetch plan entitled feature codes.
          .then(() => {
            store.dispatch('PlanModule/fetchEntitledFeatureCodes');
            updateLastActiveTime();
          })
          .then(() => this.initializeGrowthSettings())
          .then(() => this.initializeOnboarding())
          .then(() => store.subscribeAction(initializeSubscribeAction()))
          .then(resolve)).catch(() => {
          this.handleNotLoggedIn(cookieType);
          // Stop initializing.
          return reject();
        }));
    },

    handleNotLoggedIn(userType) {
      if (userType === LoginConsts.clientType.admin) {
        pageNavigationUtil.saveRequestedPage();
        pageNavigationUtil.setLocation(LoginConsts.loginRoutes.admin);
      } else {
        pageNavigationUtil.setLocation(LoginConsts.loginRoutes.user);
      }
    },

    updateSettingsAndMspState(resp) {
      // Save the org count
      store.dispatch('SettingsModule/updateSettings', { orgsCount: resp[0].length });

      settings = store.state.SettingsModule;
      const { orgId, orgsCount, user } = settings;

      // Capture the orgId for passing in request headers
      if (resp[1]) {
        MspState.orgId = orgId;
        if (user.provider) {
          MspState.providerId = user.provider;
        }
      } else {
        MspState.clearOrgId();
        MspState.clearProviderId();
      }

      // Currently, an admin is logging in as an MSP if they are associated with
      // more than 1 org or they have a provider id
      // TODO: Update this code as the definition of an MSP admin changes
      MspState.isAnMsp = (orgsCount > 1 || !!user.provider);
    },

    /**
     * Make sure the Optimizely snippet has initialized
     * @see https://docs.developers.optimizely.com/web/docs/add-listener
     */
    initializeOptimizely() {
      window.optimizely = window.optimizely || [];

      window.optimizely.push({
        type: 'addListener',
        filter: {
          type: 'lifecycle',
          name: 'activated',
        },
        handler: () => this.handleOptimizelyInitialized(),
      });
    },

    handleOptimizelyInitialized() {
      this.optimizelySetup();
      this.initializeGrowthExperiments();
    },

    optimizelySetup() {
      OptimizelyApi.initialize();
      OptimizelyHelper.initializeCustomAttributeValues();
      this.optimizelyPageView();
      store.dispatch(`${growthModule}/updateOptimizelyLoaded`, true);
    },

    optimizelyPageView() {
      settings = store.state.SettingsModule;
      const { isCommandRunner, orgId } = settings;

      // Triggers events used to begin relevant Optimizely experiments
      OptimizelyHelper.trackAdminPortalConsoleView();
      OptimizelyHelper.trackWindowsAppModal();
      OptimizelyHelper.trackSignupAdminPortalCrossoverView();

      if (!isCommandRunner && !MspState.isAnMsp && orgId) {
        OptimizelyHelper.trackNotMspNorCommandRunner();
        OptimizelyHelper.getOptimizelyAudienceTesterVariation();
      }
    },

    initializeMtp() {
      pageNavigationUtil.setLocation('/mtp');
    },

    /**
     * Used for admins that are not part of an MSP.
     * Initializes admin portal view with Viewport and Backbone
     */
    renderBackboneApp(vueApp) {
      if (Backbone.History.started) Backbone.history.stop();

      const el = '#backbone';
      // Initialize viewport...
      return new Viewport({ el, vueApp }, function initViewport() {
        this.render();

        // Initialize router and start tracking fragments.
        //
        // Note that the Backbone router is added as a member to the Viewport instance. This is
        // necessary for the home grown navigation guards to work properly in the Backbone router.

        this.router = new Router({ viewport: this });

        Backbone.history.start();

        // Set cookie to admin so next time admin goes to login page
        // they are defaulted to the admin login form.
        $.cookie('jc_prevLoginType', 'admin', util.getCookieOptions());
      });
    },

    renderVueApp(vueRouter) {
      const adminPortalContainer = document.createElement('div');
      document.body.appendChild(adminPortalContainer);

      return new Vue({
        el: adminPortalContainer,
        name: 'AdminPortalApp',
        i18n,
        render: h => h(AdminPortalApp),
        router: vueRouter,
        store,
      }).$children[0];
    },

    async renderDualApps() {
      settings = store.state.SettingsModule;
      const { orgId } = settings;
      window.orgId = hashString(orgId);

      LoggingService.debug('InitializationHandler.renderDualApp');

      const vueRouter = createVueRouter();

      // Initialize route service before loading route guards
      globalRouteService.initialize({ vueRouter });

      initializeVueRouteGuards(vueRouter);

      // locale messages need to be loaded before initializing the app, otherwise
      // the user will see the keys passed to t() flash on screen for a moment before
      // they resolve to the translated strings
      await loadAdminPortalLocales();

      const vueApp = this.renderVueApp(vueRouter);

      vueApp.$nextTick(() => {
        const backboneApp = this.renderBackboneApp(vueApp);

        this.addNavigationGuards({ backboneApp, vueApp });
      });
    },

    addNavigationGuards({ backboneApp, vueApp }) {
      pageNavigationUtil.addEventHandlers({ backboneApp, vueApp });
    },
  };
}

export default createInitializationHandler();
